import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, ShieldCheck, Smartphone } from "lucide-react";
import SiteLogo from "../../components/SiteLogo";
import PageSeo from "../../components/PageSeo";
import old1_PublicSiteFooter from "../components/old1_PublicSiteFooter";
import JobuwinPageLoading from "../../components/JobuwinPageLoading";
import TurnstileField from "../../components/TurnstileField";
import {
  isTurnstileEnabled,
  TURNSTILE_REQUIRED_MESSAGE,
  formatTurnstileErrorForUser,
} from "../../turnstileConfig";
import {
  isApiConfigured,
  postVerifyTurnstileToken,
  getPhoneVerificationLookup,
  getVerificationPhoneStatus,
  postResendPhoneVerification,
  postVerifyPhone,
} from "../../apiClient";
import { buildAuthHrefWithNext, parseSafeInternalNextPath } from "../../navigateAfterAuth";

/** Group national digits for readability (e.g. 7911123456 → 7911 123 456). */
function formatNationalDigitsForDisplay(raw) {
  const d = String(raw ?? "").replace(/\D/g, "");
  if (!d) return "";
  if (d.length <= 4) return d;
  const parts = [];
  let pos = d.length;
  const firstTake = Math.min(4, pos);
  parts.unshift(d.slice(pos - firstTake, pos));
  pos -= firstTake;
  while (pos > 0) {
    const take = Math.min(3, pos);
    parts.unshift(d.slice(pos - take, pos));
    pos -= take;
  }
  return parts.join(" ");
}

function formatPhoneWithSpacing(countryCode, mobile) {
  const cc = String(countryCode ?? "").trim();
  const national = formatNationalDigitsForDisplay(mobile);
  if (!cc && !national) return "your mobile number";
  if (cc && national) return `${cc} ${national}`.trim();
  return cc || national || String(mobile ?? "").trim();
}

function parseResendCooldownSecondsFromMessage(message) {
  const m = String(message || "").match(/(\d+)\s*seconds/);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function old1_VerifyMobilePage({ onSignIn, authToken }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const signupData = location.state?.signupData || null;
  const safeNext = useMemo(() => parseSafeInternalNextPath(searchParams.get("next")), [searchParams]);
  const signinHref = useMemo(() => buildAuthHrefWithNext("/old1/signin", safeNext), [safeNext]);
  const signupHref = useMemo(() => buildAuthHrefWithNext("/old1/signup", safeNext), [safeNext]);

  const offlineInitially =
    !isApiConfigured() || !String(authToken || "").trim();

  const [lookupLoading, setLookupLoading] = useState(
    () =>
      !offlineInitially && isApiConfigured() && Boolean(String(authToken || "").trim())
  );
  const [lookupBlocked, setLookupBlocked] = useState(false);
  const [lookupBlockedMessage, setLookupBlockedMessage] = useState("");
  const [lookupReady, setLookupReady] = useState(() => offlineInitially);
  const [offlineMode, setOfflineMode] = useState(() => offlineInitially);

  const [smsSent, setSmsSent] = useState(false);
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState("");
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileKey, setTurnstileKey] = useState(0);
  const [turnstileSession, setTurnstileSession] = useState(0);
  const turnstileSessionRef = useRef(0);
  const [resendStatus, setResendStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const [showContactSupport, setShowContactSupport] = useState(false);
  /** Server-aligned unlock time (ms) for Send SMS / Send SMS again — same cooldown rules as verify email. */
  const [smsResendLockedUntilMs, setSmsResendLockedUntilMs] = useState(null);
  const [cooldownNow, setCooldownNow] = useState(() => Date.now());
  const inputsRef = useRef([]);

  useEffect(() => {
    if (!smsResendLockedUntilMs) return undefined;
    const tick = () => {
      const now = Date.now();
      setCooldownNow(now);
      if (now >= smsResendLockedUntilMs) {
        setSmsResendLockedUntilMs(null);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [smsResendLockedUntilMs]);

  const smsCooldownActive =
    smsResendLockedUntilMs != null && cooldownNow < smsResendLockedUntilMs;
  const smsCooldownSecondsLeft = smsCooldownActive
    ? Math.max(1, Math.ceil((smsResendLockedUntilMs - cooldownNow) / 1000))
    : 0;

  const displayMobileFull = useMemo(
    () => formatPhoneWithSpacing(signupData?.countryCode, signupData?.mobile),
    [signupData?.countryCode, signupData?.mobile]
  );

  useEffect(() => {
    let cancelled = false;

    async function runLookup() {
      if (!isApiConfigured() || !String(authToken || "").trim()) {
        setOfflineMode(true);
        setLookupLoading(false);
        setLookupReady(true);
        return;
      }

      setLookupLoading(true);
      setLookupBlocked(false);
      setLookupBlockedMessage("");

      const data = await getPhoneVerificationLookup(authToken);
      if (cancelled) return;

      if (!data) {
        setLookupBlocked(true);
        setLookupBlockedMessage(
          "We could not check your phone number. Please try again or go back and confirm your details."
        );
        setLookupLoading(false);
        return;
      }

      if (data.alreadyVerified) {
        navigate(signinHref, { state: { welcome: true, signupData } });
        return;
      }

      if (!data.lookupOk) {
        setLookupBlocked(true);
        setLookupBlockedMessage(
          data.message ||
            "We couldn’t verify this number right now. Please try again in a moment or update your mobile number on sign up."
        );
        setLookupLoading(false);
        return;
      }

      const status = await getVerificationPhoneStatus(authToken);
      if (cancelled) return;

      if (status?.exhausted && status.userFound && !status.phoneVerified) {
        setShowContactSupport(true);
        setSmsResendLockedUntilMs(null);
      }
      if ((status?.sendCount || 0) > 0 && !status?.phoneVerified) {
        setSmsSent(true);
      }
      const until = status?.resendLockedUntil;
      if (
        typeof until === "number" &&
        until > Date.now() &&
        !status?.exhausted
      ) {
        setSmsResendLockedUntilMs(until);
      } else if (!status?.exhausted) {
        setSmsResendLockedUntilMs(null);
      }

      setLookupReady(true);
      setLookupLoading(false);
    }

    runLookup();
    return () => {
      cancelled = true;
    };
  }, [authToken, displayMobileFull, navigate, signupData, signinHref]);

  const showTurnstile =
    isTurnstileEnabled() &&
    isApiConfigured() &&
    lookupReady &&
    !lookupBlocked &&
    !showContactSupport &&
    !offlineMode;

  /** Human check before first SMS — shown above the orange Send SMS button. */
  const showTurnstileBeforeFirstSms = showTurnstile && !smsSent;
  /** Human check again for Verify / resend after code was requested. */
  const showTurnstileAfterSms = showTurnstile && smsSent;

  const codeInputsEnabled =
    lookupReady && !lookupBlocked && !lookupLoading && smsSent && !showContactSupport && !offlineMode;

  const updateDigit = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...digits];
    next[index] = value;
    setDigits(next);
    setError("");

    if (value && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, event) => {
    if (event.key === "Backspace" && !digits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
    if (event.key === "Enter" && smsSent && index === 5 && digits.join("").length === 6) {
      event.preventDefault();
      handlePrimaryAction();
    }
  };

  const handlePaste = (event) => {
    if (!codeInputsEnabled) return;
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    event.preventDefault();
    const next = ["", "", "", "", "", ""];
    for (let i = 0; i < pasted.length; i += 1) {
      next[i] = pasted[i];
    }
    setDigits(next);
    setError("");
    const focusIndex = Math.min(pasted.length, 5);
    inputsRef.current[focusIndex]?.focus();
  };

  const runHumanCheckIfNeeded = async () => {
    if (!isTurnstileEnabled() || !isApiConfigured()) return;
    if (!turnstileToken) {
      throw new Error(TURNSTILE_REQUIRED_MESSAGE);
    }
    await postVerifyTurnstileToken(turnstileToken);
  };

  const bumpTurnstileSession = () => {
    if (!isTurnstileEnabled() || !isApiConfigured()) return;
    turnstileSessionRef.current += 1;
    setTurnstileSession(turnstileSessionRef.current);
    setTurnstileKey((k) => k + 1);
    setTurnstileToken(null);
  };

  const handleTurnstileTokenChange = (token, widgetSessionId) => {
    if (typeof widgetSessionId === "number" && widgetSessionId !== turnstileSessionRef.current) {
      return;
    }
    setTurnstileToken(token);
    if (token) {
      setError((prev) => (prev === TURNSTILE_REQUIRED_MESSAGE ? "" : prev));
    }
  };

  const navigateAfterVerify = (user) => {
    const base = signupData || {};
    const nextSignupData = {
      ...base,
      phoneVerified: true,
      email: user?.email ?? base.email,
      fullName: user?.name ?? base.fullName,
      signupType: user?.role === "hiring-manager" ? "hiring-manager" : "applicant",
      countryCode: user?.countryCode ?? base.countryCode,
      mobile: user?.mobile ?? base.mobile,
      emailVerified: user?.emailVerified ?? base.emailVerified ?? true,
      password: base.password,
    };
    navigate(signinHref, {
      state: {
        welcome: true,
        signupData: nextSignupData,
      },
    });
  };

  const handleSendSms = async () => {
    setError("");
    setResendStatus("");
    setBusy(true);
    try {
      if (!isApiConfigured() || !String(authToken || "").trim()) {
        throw new Error("Not signed in. Return to sign up and try again.");
      }
      if (isTurnstileEnabled() && !turnstileToken) {
        throw new Error(TURNSTILE_REQUIRED_MESSAGE);
      }
      const turnstilePayload =
        isTurnstileEnabled() && turnstileToken ? String(turnstileToken) : undefined;
      await postResendPhoneVerification({
        turnstileToken: turnstilePayload,
        authToken,
      });
      setSmsSent(true);
      setResendStatus("Check your phone for a text message with your code.");
      bumpTurnstileSession();
      const st = await getVerificationPhoneStatus(authToken);
      if (st?.exhausted && st.userFound && !st.phoneVerified) {
        setShowContactSupport(true);
        setSmsResendLockedUntilMs(null);
      } else {
        const until = st?.resendLockedUntil;
        if (typeof until === "number" && until > Date.now()) {
          setSmsResendLockedUntilMs(until);
        } else {
          setSmsResendLockedUntilMs(null);
        }
      }
    } catch (err) {
      const c = err && typeof err === "object" && "code" in err ? err.code : undefined;
      if (c === "PHONE_SMS_SENDS_EXHAUSTED") {
        setShowContactSupport(true);
        setSmsResendLockedUntilMs(null);
        setError("");
      } else if (c === "RESEND_COOLDOWN") {
        const sec = parseResendCooldownSecondsFromMessage(
          err instanceof Error ? err.message : ""
        );
        if (sec != null) {
          setSmsResendLockedUntilMs(Date.now() + sec * 1000);
        }
        setError("");
      } else if (c === "LOOKUP_REQUIRED") {
        setError("Please refresh this page so we can validate your number again.");
      } else {
        const raw = err instanceof Error ? err.message : "Could not send SMS";
        setError(formatTurnstileErrorForUser(raw));
        bumpTurnstileSession();
      }
    } finally {
      setBusy(false);
    }
  };

  const handleVerifySubmit = async () => {
    const code = digits.join("");
    if (code.length !== 6) {
      setError("Enter the full 6-digit code.");
      return;
    }

    setError("");
    setResendStatus("");
    setBusy(true);
    try {
      if (!isApiConfigured() || !String(authToken || "").trim()) {
        throw new Error("Not signed in.");
      }
      if (isTurnstileEnabled() && !turnstileToken) {
        throw new Error(TURNSTILE_REQUIRED_MESSAGE);
      }
      const turnstilePayload =
        isTurnstileEnabled() && turnstileToken ? String(turnstileToken) : undefined;
      const { user } = await postVerifyPhone({
        code,
        turnstileToken: turnstilePayload,
        authToken,
      });
      if (typeof onSignIn === "function") {
        onSignIn(user);
      }
      navigateAfterVerify(user);
    } catch (err) {
      const c = err && typeof err === "object" && "code" in err ? err.code : undefined;
      if (c === "PHONE_SMS_SENDS_EXHAUSTED") {
        setShowContactSupport(true);
        setSmsResendLockedUntilMs(null);
        setError("");
      } else {
        const raw = err instanceof Error ? err.message : "Verification failed";
        setError(formatTurnstileErrorForUser(raw));
        bumpTurnstileSession();
      }
    } finally {
      setBusy(false);
    }
  };

  const handleOfflineContinue = async () => {
    setError("");
    setBusy(true);
    try {
      await runHumanCheckIfNeeded();
      navigateAfterVerify({
        email: signupData?.email,
        name: signupData?.fullName,
        role: signupData?.signupType === "hiring-manager" ? "hiring-manager" : "applicant",
        countryCode: signupData?.countryCode,
        mobile: signupData?.mobile,
        emailVerified: true,
      });
    } catch (err) {
      const raw = err instanceof Error ? err.message : "Verification failed";
      setError(formatTurnstileErrorForUser(raw));
    } finally {
      setBusy(false);
    }
  };

  const handlePrimaryAction = () => {
    if (offlineMode) {
      handleOfflineContinue();
      return;
    }
    if (smsSent) {
      handleVerifySubmit();
    }
  };

  const handleResendLink = async () => {
    if (showContactSupport || !smsSent) return;
    setError("");
    setResendStatus("");
    setBusy(true);
    try {
      if (isTurnstileEnabled() && !turnstileToken) {
        throw new Error(TURNSTILE_REQUIRED_MESSAGE);
      }
      const turnstilePayload =
        isTurnstileEnabled() && turnstileToken ? String(turnstileToken) : undefined;
      await postResendPhoneVerification({
        turnstileToken: turnstilePayload,
        authToken,
      });
      setResendStatus("We sent another code by SMS.");
      bumpTurnstileSession();
      const st = await getVerificationPhoneStatus(authToken);
      if (st?.exhausted && st.userFound && !st.phoneVerified) {
        setShowContactSupport(true);
        setSmsResendLockedUntilMs(null);
      } else {
        const until = st?.resendLockedUntil;
        if (typeof until === "number" && until > Date.now()) {
          setSmsResendLockedUntilMs(until);
        } else {
          setSmsResendLockedUntilMs(null);
        }
      }
    } catch (err) {
      const c = err && typeof err === "object" && "code" in err ? err.code : undefined;
      if (c === "PHONE_SMS_SENDS_EXHAUSTED") {
        setShowContactSupport(true);
        setSmsResendLockedUntilMs(null);
        setError("");
      } else if (c === "RESEND_COOLDOWN") {
        const sec = parseResendCooldownSecondsFromMessage(
          err instanceof Error ? err.message : ""
        );
        if (sec != null) {
          setSmsResendLockedUntilMs(Date.now() + sec * 1000);
        }
        setError("");
      } else {
        const raw = err instanceof Error ? err.message : "Request failed";
        setError(formatTurnstileErrorForUser(raw));
        bumpTurnstileSession();
      }
    } finally {
      setBusy(false);
    }
  };

  const primaryLabel = (() => {
    if (busy) return "Please wait…";
    if (offlineMode) return "Continue to sign in";
    if (!smsSent) return "Verify mobile";
    return "Verify";
  })();

  const primaryDisabled = (() => {
    if (busy) return true;
    if (showContactSupport || lookupBlocked) return true;
    if (lookupLoading || !lookupReady) return true;
    if (offlineMode) return false;
    if (!offlineMode && !smsSent) return true;
    if (smsSent) {
      if (digits.join("").length !== 6) return true;
    }
    return false;
  })();

  const topSendSmsDisabled =
    busy ||
    smsCooldownActive ||
    (!offlineMode &&
      (!lookupReady || lookupLoading || lookupBlocked || showContactSupport)) ||
    (showTurnstileBeforeFirstSms && !turnstileToken);

  const showPrimary =
    !showContactSupport && !lookupBlocked && (lookupReady || offlineMode) && !lookupLoading;

  return (
    <div className="signup-page jw-public-page jw-auth-page">
      <PageSeo
        title="Verify your mobile number"
        description="Confirm your mobile number to secure your JOBUWIN account."
        path="/old1/verify-mobile"
        noindex
      />
      <nav className="landing-nav jw-public-nav" aria-label="Mobile verification navigation">
        <div className="landing-nav-shell jw-public-nav__shell">
          <SiteLogo />
          <div className="nav-buttons landing-nav-actions">
            <button type="button" className="jw-public-button jw-public-button--text landing-nav-login" onClick={() => navigate(signinHref)}>
              Sign in
            </button>
            <button type="button" className="jw-public-button jw-public-button--primary landing-nav-cta" onClick={() => navigate(signupHref)}>
              Sign up
              <ArrowRight className="jw-public-icon" aria-hidden="true" />
            </button>
          </div>
        </div>
      </nav>

      <main id="main-content" className="signup-container jw-auth-shell">
        <aside className="jw-auth-aside" aria-label="Mobile verification guidance">
          <span className="jw-public-chip">
            <ShieldCheck className="jw-public-icon" aria-hidden="true" />
            Verify your mobile
          </span>
          <h1>Keep JOBUWIN accounts tied to real people.</h1>
          <p>
            Mobile verification adds another layer of trust before you enter the applicant or hiring workspace.
          </p>
          <div className="jw-auth-benefits">
            <span><CheckCircle2 className="jw-public-icon" aria-hidden="true" /> SMS code sent to your number</span>
            <span><CheckCircle2 className="jw-public-icon" aria-hidden="true" /> Resend cooldown protects delivery</span>
            <span><CheckCircle2 className="jw-public-icon" aria-hidden="true" /> Support path if verification fails</span>
          </div>
        </aside>

        <section className="signup-card verify-mobile-card jw-public-card jw-auth-card">
          <Link
            className="back-link jw-public-back-link"
            to={showContactSupport ? "/home" : signupHref}
            state={
              showContactSupport ? undefined : signupData ? { signupData } : undefined
            }
          >
            <ArrowLeft className="jw-public-icon" aria-hidden="true" />
            {showContactSupport ? "Back to home" : "Back to sign up"}
          </Link>
          <p className="eyebrow jw-public-eyebrow">Mobile verification</p>
          <h2 className={showContactSupport ? "help-contact-heading" : undefined}>
            {showContactSupport
              ? "We are sorry. It looks like the system is not working correctly for you."
              : lookupBlocked
                ? "We couldn’t verify this number"
                : "Verify your mobile number"}
          </h2>

          {lookupLoading && isApiConfigured() && String(authToken || "").trim() ? (
            <JobuwinPageLoading
              compact
              title="Checking your phone number…"
              subtitle="Please wait a moment."
            />
          ) : null}

          {!showContactSupport && lookupBlocked ? (
            <div role="alert">
              <p className="subtitle" style={{ whiteSpace: "pre-line" }}>
                {lookupBlockedMessage}
              </p>
              <p className="change-email-wrap" style={{ marginTop: "1rem" }}>
                <Link
                  to={signupHref}
                  className="change-email-link"
                  state={signupData ? { signupData } : undefined}
                >
                  Update mobile number on sign up
                </Link>
              </p>
            </div>
          ) : null}

          {!showContactSupport && !lookupBlocked && !lookupLoading ? (
            <>
              <div className="verify-mobile-intro">
                {offlineMode ? (
                  <p className="verify-mobile-intro__line">
                    API is not configured or you are not signed in. Use the security check below and
                    continue (development only).
                  </p>
                ) : smsSent ? (
                  <p className="verify-mobile-intro__line">
                    Enter the 6-digit code we sent you by SMS.
                  </p>
                ) : (
                  <>
                    <p className="verify-mobile-intro__line">
                      Tap <strong>Send SMS</strong> below. We&apos;ll send a verification code to{" "}
                      <strong className="verify-mobile-phone-display">{displayMobileFull}</strong>.
                    </p>
                  </>
                )}
              </div>
              <p className="change-email-wrap verify-mobile-change-number">
                <Link
                  to={signupHref}
                  className="change-email-link"
                  state={signupData ? { signupData } : undefined}
                >
                  change mobile number
                </Link>
              </p>

              {showTurnstileBeforeFirstSms ? (
                <div className="verify-mobile-turnstile-before-send">
                  {error === TURNSTILE_REQUIRED_MESSAGE ? (
                    <p className="turnstile-inline-alert" role="alert">
                      {error}
                    </p>
                  ) : null}
                  <TurnstileField
                    tokenSessionId={turnstileSession}
                    onTokenChange={handleTurnstileTokenChange}
                    widgetKey={`verify-mobile-pre-${turnstileKey}`}
                  />
                  <p className="turnstile-human-note">
                    Security check: making sure you are a genuine human :-)
                  </p>
                </div>
              ) : null}

              {!offlineMode && !smsSent && lookupReady ? (
                <div className="verify-mobile-send-sms-top-wrap">
                  <button
                    type="button"
                    className="verify-mobile-send-sms-top"
                    onClick={handleSendSms}
                    disabled={topSendSmsDisabled}
                  >
                    {busy
                      ? "Please wait…"
                      : smsCooldownActive
                        ? `Wait ${smsCooldownSecondsLeft}s`
                        : "Send SMS"}
                  </button>
                </div>
              ) : null}
            </>
          ) : null}

          {showContactSupport ? (
            <div className="help-contact-panel" role="alert">
              <p className="help-contact-panel__text">
                Please email{" "}
                <a className="help-contact-panel__link" href="mailto:help@jobuwin.com">
                  help@jobuwin.com
                </a>{" "}
                and we&apos;ll help you with the next steps.
              </p>
              <p className="help-contact-panel__footer">
                <Link className="change-email-link" to="/old1/home">
                  Back to home
                </Link>
              </p>
            </div>
          ) : null}

          {!showContactSupport && !lookupBlocked && (lookupReady || offlineMode) && !lookupLoading ? (
            <div className="signup-form jw-auth-form jw-auth-code-form">
              <label
                htmlFor="mobile-code-0"
                style={{ opacity: codeInputsEnabled ? 1 : 0.55 }}
              >
                Verification code
              </label>
              <div className="jw-auth-code-heading">
                <Smartphone className="jw-public-icon" aria-hidden="true" />
                Enter the SMS code
              </div>
              <div className="code-inputs" onPaste={handlePaste}>
                {digits.map((digit, index) => (
                  <input
                    key={`mobile-digit-${index}`}
                    id={`mobile-code-${index}`}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={1}
                    value={digit}
                    onChange={(event) => updateDigit(index, event.target.value)}
                    onKeyDown={(event) => handleKeyDown(index, event)}
                    ref={(element) => {
                      inputsRef.current[index] = element;
                    }}
                    disabled={!codeInputsEnabled}
                    aria-label={`Mobile verification digit ${index + 1}`}
                    aria-disabled={!codeInputsEnabled}
                  />
                ))}
              </div>

              {error && error !== TURNSTILE_REQUIRED_MESSAGE ? (
                <p
                  className="field-note"
                  role="alert"
                  style={{ color: "#b42318", whiteSpace: "pre-line" }}
                >
                  {error}
                </p>
              ) : null}

              {showTurnstileAfterSms ? (
                <>
                  {error === TURNSTILE_REQUIRED_MESSAGE ? (
                    <p className="turnstile-inline-alert" role="alert">
                      {error}
                    </p>
                  ) : null}
                  <TurnstileField
                    tokenSessionId={turnstileSession}
                    onTokenChange={handleTurnstileTokenChange}
                    widgetKey={`verify-mobile-${turnstileKey}`}
                  />
                  <p className="turnstile-human-note">
                    Security check: making sure you are a genuine human :-)
                  </p>
                </>
              ) : null}

              {showPrimary ? (
                <button
                  type="button"
                  className="submit-button jw-public-button jw-public-button--primary"
                  onClick={handlePrimaryAction}
                  disabled={
                    primaryDisabled ||
                    (showTurnstileAfterSms && !turnstileToken && !busy)
                  }
                >
                  {primaryLabel}
                  <ArrowRight className="jw-public-icon" aria-hidden="true" />
                </button>
              ) : null}

              {smsSent && !offlineMode && !showContactSupport ? (
                <div className="verify-mobile-send-sms-top-wrap verify-mobile-send-sms-top-wrap--after">
                  <button
                    type="button"
                    className="verify-mobile-send-sms-top"
                    onClick={handleResendLink}
                    disabled={
                      busy ||
                      smsCooldownActive ||
                      (showTurnstileAfterSms && !turnstileToken)
                    }
                  >
                    {busy
                      ? "Please wait…"
                      : smsCooldownActive
                        ? `Wait ${smsCooldownSecondsLeft}s`
                        : "Send SMS again"}
                  </button>
                </div>
              ) : null}

              {resendStatus ? (
                <p
                  className="field-note"
                  style={{ marginTop: "1.25rem", textAlign: "center" }}
                >
                  {resendStatus}
                </p>
              ) : null}
            </div>
          ) : null}
        </section>
      </main>

      <old1_PublicSiteFooter />
    </div>
  );
}

export default old1_VerifyMobilePage;
